import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { NOTIFICATION_CATEGORIES, type NotificationCategory } from '@neuroedge/shared';
import { Card, PageState, useToast } from '../../components/ui';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '../../api/notifications';

/**
 * "Notifications" on the Profile page: one switch per category, saved as
 * soon as it's flipped. Applies to this workspace only. Reached directly via
 * the bell's "Notification settings" link (`/profile#notifications`).
 */
export function NotificationPreferencesCard() {
  const prefs = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();
  const toast = useToast();
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (location.hash === '#notifications' && prefs.data) {
      ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash, prefs.data]);

  async function toggle(key: NotificationCategory, on: boolean) {
    try {
      await update.mutateAsync({ [key]: on });
      toast.success(on ? 'Notifications turned on.' : 'Notifications turned off.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save this setting.');
    }
  }

  return (
    <div id="notifications" ref={ref}>
      <Card title="Notifications">
        {prefs.isPending ? (
          <PageState kind="loading" />
        ) : prefs.isError ? (
          <PageState
            kind="error"
            message={prefs.error.message}
            onRetry={() => void prefs.refetch()}
          />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            <p
              style={{
                margin: 0,
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              Choose what shows up in your bell. Things assigned to you always come to you; anything
              unassigned goes to everyone who can see that area.
            </p>
            {NOTIFICATION_CATEGORIES.map((c) => (
              <label
                key={c.key}
                style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}
              >
                <input
                  type="checkbox"
                  checked={prefs.data[c.key]}
                  disabled={update.isPending}
                  onChange={(e) => void toggle(c.key, e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong>{c.label}</strong>
                  <br />
                  <span
                    style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
                  >
                    {c.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
